import type { ITelemetryItem } from "@microsoft/applicationinsights-web";

/**
 * LAR-435: `ResizeObserver loop completed with undelivered notifications` was
 * ~98% of all exception rows in `appi-markup-prod` — 750k rows in 14 days,
 * 299k of them from a single user in one day. The browser emits it once per
 * frame for as long as a layout oscillates, it carries no stack, and it is
 * never actionable on its own, so a flooded day buries every real regression
 * in the daily totals.
 *
 * One row per page load is kept, so the notice still shows up in App Insights
 * at ~1/100,000th of the ingestion. A dashboard that genuinely fails to settle
 * reports itself through `useDashboardGridContainerWidth` instead, which knows
 * what was looping.
 */

/** Chrome/Edge say "loop completed with undelivered notifications"; older Chrome and Safari say "loop limit exceeded". */
const RESIZE_OBSERVER_LOOP_MESSAGE = /resizeobserver loop (completed|limit)/i;

const EXCEPTION_BASE_TYPE = "ExceptionData";

/** Stamped on the row that is kept, so nobody reads it as "it only happened once". */
export const RESIZE_OBSERVER_LOOP_SAMPLING_PROPERTY =
  "resizeObserverLoopSampling";

export const RESIZE_OBSERVER_LOOP_SAMPLING = "first-per-page-load";

interface ExceptionDetail {
  typeName?: string;
  message?: string;
}

function exceptionTexts(item: ITelemetryItem): string[] {
  const exceptions: unknown = item.baseData?.exceptions;
  if (!Array.isArray(exceptions)) return [];

  return (exceptions as ExceptionDetail[])
    .flatMap((detail) => [detail?.typeName, detail?.message])
    .filter((text): text is string => typeof text === "string");
}

export function isResizeObserverLoopError(item: ITelemetryItem): boolean {
  if (item.baseType !== EXCEPTION_BASE_TYPE) return false;
  return exceptionTexts(item).some((text) =>
    RESIZE_OBSERVER_LOOP_MESSAGE.test(text)
  );
}

/**
 * App Insights telemetry initializer: `false` drops the item. Stateful — it
 * counts one page load — so create it once, next to `loadAppInsights()`.
 */
export function createResizeObserverLoopFilter(): (
  item: ITelemetryItem
) => boolean {
  let kept = false;

  return (item) => {
    if (!isResizeObserverLoopError(item)) return true;
    if (kept) return false;

    kept = true;
    item.data = {
      ...item.data,
      [RESIZE_OBSERVER_LOOP_SAMPLING_PROPERTY]: RESIZE_OBSERVER_LOOP_SAMPLING,
    };
    return true;
  };
}
